import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react"
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from "react-zoom-pan-pinch"
import { Slider } from "components/Slider"
import * as Icons from "@nova/component-icons"
import { IconButton } from "@nova/component-btn"

const VIEWPORT = 300
const OUTPUT = 250
const MIN_ZOOM = 1
const MAX_ZOOM = 5

export type ProfilePhotoCropperHandle = {
    getCroppedFile: () => Promise<File>
}

export const ProfilePhotoCropper = forwardRef<ProfilePhotoCropperHandle, { file: File }>(
    ({ file }, ref) => {
        const [source, setSource] = useState<HTMLImageElement>()
        const [rotation, setRotation] = useState(0)
        const [zoom, setZoom] = useState(MIN_ZOOM)
        const transformRef = useRef<ReactZoomPanPinchRef>(null)

        useEffect(() => {
            const url = URL.createObjectURL(file)
            const img = new Image()
            img.onload = () => setSource(img)
            img.src = url
            return () => URL.revokeObjectURL(url)
        }, [file])

        // cover-fit scale so the image fills the square after rotation; the <img> is
        // rendered at its unrotated size and CSS rotate() turns it to cover the square
        const cover = source ? VIEWPORT / Math.min(source.width, source.height) : 1
        const dispW = (source?.width ?? 0) * cover
        const dispH = (source?.height ?? 0) * cover
        // the rotated image's axis-aligned bounding box; this is the content size
        // limitToBounds uses to decide how far the image can pan
        const swap = Math.abs(rotation % 180) === 90
        const boxW = swap ? dispH : dispW
        const boxH = swap ? dispW : dispH

        const applyZoom = useCallback(
            (nextZoom: number) => {
                const api = transformRef.current
                if (!api) return
                const { scale, positionX, positionY } = api.instance.transformState
                // zoom about the viewport center, clamped per-axis so the scaled box keeps
                // covering the viewport (box can be larger than VIEWPORT on either axis)
                const center = VIEWPORT / 2
                const factor = nextZoom / scale
                const clampAxis = (v: number, box: number) =>
                    Math.min(0, Math.max(VIEWPORT - box * nextZoom, v))
                api.setTransform(
                    clampAxis(center - (center - positionX) * factor, boxW),
                    clampAxis(center - (center - positionY) * factor, boxH),
                    nextZoom,
                    0
                )
                setZoom(nextZoom)
            },
            [boxW, boxH]
        )

        useImperativeHandle(ref, () => ({
            getCroppedFile: async () => {
                const api = transformRef.current
                if (!source || !api) throw new Error("Image not loaded")
                const { scale, positionX, positionY } = api.instance.transformState
                const canvas = document.createElement("canvas")
                canvas.width = OUTPUT
                canvas.height = OUTPUT
                const ctx = canvas.getContext("2d")!
                const ratio = OUTPUT / VIEWPORT
                // viewport(px) -> output, undo pan/zoom, then reproduce the displayed
                // image: centered, rotated, cover-fit scaled
                ctx.translate(positionX * ratio, positionY * ratio)
                ctx.scale(scale * ratio, scale * ratio)
                ctx.translate(boxW / 2, boxH / 2)
                ctx.rotate((rotation * Math.PI) / 180)
                ctx.scale(cover, cover)
                ctx.drawImage(source, -source.width / 2, -source.height / 2)
                const blob = await new Promise<Blob | null>((res) =>
                    canvas.toBlob(res, "image/jpeg", 0.9)
                )
                if (!blob) throw new Error("Canvas is empty")
                return new File([blob], `profile-photo-${crypto.randomUUID()}.jpg`, {
                    type: "image/jpeg",
                })
            },
        }))

        return (
            <div className="flex flex-col gap-2 items-center w-[300px]">
                <div className="w-[300px] h-[300px] relative bg-gray-900 overflow-hidden rounded-2xl">
                    {source && (
                        <TransformWrapper
                            ref={transformRef}
                            minScale={MIN_ZOOM}
                            maxScale={MAX_ZOOM}
                            centerOnInit
                            limitToBounds
                            doubleClick={{ disabled: true }}
                            wheel={{ step: 0.1 }}
                            panning={{ velocityDisabled: true }}
                            onTransformed={(r) => setZoom(r.state.scale)}
                        >
                            <TransformComponent
                                wrapperStyle={{ width: VIEWPORT, height: VIEWPORT }}
                                contentStyle={{ width: boxW, height: boxH, position: "relative" }}
                            >
                                <img
                                    src={source.src}
                                    alt=""
                                    draggable={false}
                                    className="absolute left-1/2 top-1/2 max-w-none select-none"
                                    style={{
                                        width: dispW,
                                        height: dispH,
                                        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                                    }}
                                />
                            </TransformComponent>
                        </TransformWrapper>
                    )}
                </div>
                <div className="flex items-center justify-between gap-3 w-full">
                    <div className="flex items-center gap-2 flex-1">
                        <IconButton onClick={() => applyZoom(Math.max(MIN_ZOOM, zoom - 0.2))}>
                            <Icons.SmallIcon Icon={Icons.ZoomOut} />
                        </IconButton>
                        <Slider
                            className="flex-1 max-w-none"
                            min={MIN_ZOOM}
                            max={MAX_ZOOM}
                            step={0.01}
                            value={zoom}
                            onChange={applyZoom}
                        />
                        <IconButton onClick={() => applyZoom(Math.min(MAX_ZOOM, zoom + 0.2))}>
                            <Icons.SmallIcon Icon={Icons.ZoomIn} />
                        </IconButton>
                    </div>
                    <div className="flex items-center gap-2">
                        <IconButton onClick={() => setRotation((r) => r + 90)}>
                            <Icons.SmallIcon Icon={Icons.Rotate90DegreesCW} />
                        </IconButton>
                        <IconButton onClick={() => setRotation((r) => r - 90)}>
                            <Icons.SmallIcon Icon={Icons.Rotate90DegreesCCW} />
                        </IconButton>
                    </div>
                </div>
            </div>
        )
    }
)

ProfilePhotoCropper.displayName = "ProfilePhotoCropper"
